import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * Sine kernel — verbatim port of SpacesDepartment._processSineParticle (the
 * drag-profile hotspot this whole type started from: the reference version
 * ran a bound-closure forEach + per-particle method dispatch + a fresh
 * frame-sizes object per frame; profiled at 6.1% of busy time under drag
 * before the quick-wins commit, still object-pipeline-bound after it).
 *
 * The per-FRAME state machine stays in SpacesDepartment (the fork of
 * _processSineWave: sineIndex/xIncrease advance + direction flips); this
 * kernel is the per-PARTICLE loop with every frame-constant hoisted into the
 * SineFrame struct once per tick:
 *  - the isometric grid positions are flattened Float32Array pairs (built
 *    once per department construction — the reference builds the object
 *    array once in its constructor);
 *  - the sine lookup's y channel is a flattened Float32Array (SineLookup's
 *    table is constructor-built and immutable);
 *  - frame sizes (x1/x2) come from the reuse-object _getFrameSizes pattern
 *    the reference quick-wins commit added — computed once per tick;
 *  - getRelativeHeight(px) = px / innerHeight — the divide stays in-loop
 *    (the lookup y varies per particle), innerHeight is frame-constant.
 *
 * Reference math, kept bug-compatible:
 *  - transformedIndex = round(sineIndex) + round(len * (i / total)), single
 *    wrap subtract (roundedIndex < len + 1, offset <= len — one subtract
 *    always lands in range; the reference's `!lookup[t]` guard can then
 *    never fire and is not ported);
 *  - xRatio = isoX / 1.4 clamped to 0..1 (never clamps with the stock
 *    isometric grid — kept anyway);
 *  - 'random': yThing = lookup[floor(xRatio * len)].y (floor(1 * len) would
 *    read past the table — unreachable per the clamp note, like the
 *    reference);
 *  - sineSeed: the reference rolls random(0.4, 0.8) per particle and then
 *    overwrites it with the CONSTANT 5 (bug kept — the department keeps the
 *    dead roll for RNG-stream parity and passes the scalar here);
 *  - scale floor 0.1; hardSetScale (value = target = saved);
 *  - position writes are SOFT rel-lane writes (position.x/y setters) — the
 *    progressParallax kernel turns them into px targets the same tick (the
 *    department ticks before the kernels, like reference field-before-
 *    particles order).
 */

export const SINE_NORMAL = 0;
export const SINE_RANDOM = 1;
export const SINE_WAVEY = 2;

/** Per-frame scalars, resolved ONCE by SpacesDepartment.tick. */
export interface SineFrame {
	type: number; //SINE_* (the reference's _sineType string)
	xIncrease: number; //_xIncrease (department-advanced per frame)
	roundedIndex: number; //Math.round(_sineIndex)
	total: number; //_spaceSineParticles.length
	frameX1: number; //_frameSizes.x1
	frameX2: number; //_frameSizes.x2
	innerHeight: number; //device.innerHeight (getRelativeHeight's divisor)
	sineSeed: number; //the constant 5 (see header)
}

export function sineKernel(
	core: ParticleCore,
	poolStart: number,
	isoX: Float32Array,
	isoY: Float32Array,
	sineY: Float32Array,
	frame: SineFrame
): void {
	const length = sineY.length;
	const total = frame.total;
	const roundedIndex = frame.roundedIndex;
	const xIncrease = frame.xIncrease;
	const type = frame.type;
	//Kept as a divide in-loop (reciprocal-multiply is not bit-identical):
	const innerHeight = frame.innerHeight;
	const frameX1 = frame.frameX1;
	//(x2 - x1) * 0.75 — the reference recomputes it per particle off the
	//shared frame object; frame-constant, hoisted (output-identical):
	const spanX = (frame.frameX2 - frame.frameX1) * 0.75;
	const sineSeed = frame.sineSeed;

	for (let i = 0; i < total; i++) {
		let transformedIndex = roundedIndex + Math.round(length * (i / total));
		if (transformedIndex >= length) {
			transformedIndex -= length;
		}

		const rawX = isoX[i];

		let xRatio = rawX / 1.4;
		if (xRatio > 1) xRatio = 1;
		else if (xRatio < 0) xRatio = 0;

		let xYIncrease = 1;
		let scaleA = 2;

		if (type === SINE_RANDOM) {
			const yThing = sineY[Math.floor(xRatio * length)];
			xYIncrease = yThing * 1 * xIncrease;
		} else if (type === SINE_WAVEY) {
			xYIncrease = rawX * xIncrease;
			scaleA = 0.7;
		}

		const yEffect = (sineY[transformedIndex] / innerHeight) * (3 + xYIncrease) * sineSeed;
		let scale = 0.15 + yEffect * scaleA;

		if (scale < 0.1) scale = 0.1;

		const lane = poolStart + i;

		//position.y = ... (SOFT rel write):
		core.relY[lane] = isoY[i] + yEffect * -1;

		//hardSetScale(scale):
		core.scale[lane] = scale;
		core.tScale[lane] = scale;
		core.tScaleSaved[lane] = scale;

		//position.x = ... (SOFT rel write):
		core.relX[lane] = frameX1 + rawX * spanX;
	}
}
